import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, X, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useI18n } from "../../lib/i18n";

/** Sticky lead-generation widget: Call / WhatsApp / Callback in bottom-right corner. */
const FloatingContact = ({ settings }) => {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [callback, setCallback] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", topic: "" });
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!settings) return null;

  const phone = settings.contact_phone || "";
  const wa = (settings.whatsapp_number || "").replace(/[^\d]/g, "");
  const waMsg = encodeURIComponent(settings.whatsapp_message || "Merhaba");
  const cbEnabled = settings.callback_enabled !== false;

  const submitCallback = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/callback", { ...form, lang });
      toast.success(t("common.applyReceived"));
      setForm({ name: "", phone: "", topic: "" });
      setCallback(false);
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err.message);
    } finally {
      setBusy(false);
    }
  };

  const labels = {
    tr: { call: "Hemen Ara", wa: "WhatsApp", cb: "Sizi Arayalım", cbTitle: "Sizi Arayalım", name: "Adınız", phone: "Telefonunuz", topic: "Konu (opsiyonel)", send: "Talep Gönder" },
    en: { call: "Call Now", wa: "WhatsApp", cb: "Request Callback", cbTitle: "Request a Callback", name: "Your Name", phone: "Your Phone", topic: "Topic (optional)", send: "Send Request" },
    ar: { call: "اتصل الآن", wa: "واتساب", cb: "اطلب الاتصال", cbTitle: "اطلب اتصالاً", name: "الاسم", phone: "الهاتف", topic: "الموضوع (اختياري)", send: "إرسال" },
  };
  const L = labels[lang] || labels.tr;

  return (
    <div className="fixed bottom-6 right-6 z-40" data-testid="floating-contact" dir="ltr" style={{ direction: "ltr" }}>
      <AnimatePresence>
        {open && !callback ? (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="absolute bottom-16 right-0 bg-white border border-black/10 shadow-2xl w-64 overflow-hidden"
            data-testid="contact-menu"
          >
            {phone ? (
              <a href={`tel:${phone}`} className="flex items-center gap-4 px-5 py-4 border-b border-black/5 hover:bg-brand-light transition-colors" data-testid="fc-call">
                <div className="w-9 h-9 bg-brand-navy text-white flex items-center justify-center"><Phone size={16} strokeWidth={1.5} /></div>
                <div>
                  <div className="text-brand-navy font-medium">{L.call}</div>
                  <div className="text-xs text-brand-medium">{phone}</div>
                </div>
              </a>
            ) : null}
            {wa ? (
              <a href={`https://wa.me/${wa}?text=${waMsg}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 px-5 py-4 border-b border-black/5 hover:bg-brand-light transition-colors" data-testid="fc-whatsapp">
                <div className="w-9 h-9 bg-[#25D366] text-white flex items-center justify-center"><MessageCircle size={16} strokeWidth={1.5} /></div>
                <div>
                  <div className="text-brand-navy font-medium">{L.wa}</div>
                  <div className="text-xs text-brand-medium">+{wa}</div>
                </div>
              </a>
            ) : null}
            {cbEnabled ? (
              <button type="button" onClick={() => setCallback(true)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-brand-light transition-colors text-left" data-testid="fc-callback">
                <div className="w-9 h-9 bg-brand-gold text-white flex items-center justify-center"><PhoneCall size={16} strokeWidth={1.5} /></div>
                <div>
                  <div className="text-brand-navy font-medium">{L.cb}</div>
                  <div className="text-xs text-brand-medium">2 dakikada</div>
                </div>
              </button>
            ) : null}
          </motion.div>
        ) : null}

        {open && callback ? (
          <motion.form
            key="cb"
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            onSubmit={submitCallback}
            className="absolute bottom-16 right-0 bg-white border border-black/10 shadow-2xl w-72 p-6"
            data-testid="callback-form"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow eyebrow-gold">Fırat</div>
                <div className="font-serif text-lg text-brand-navy mt-1">{L.cbTitle}</div>
              </div>
              <button type="button" onClick={() => setCallback(false)} aria-label="Close" className="text-brand-medium hover:text-brand-navy" data-testid="cb-close">
                <X size={16} />
              </button>
            </div>
            <input
              required
              placeholder={L.name}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full mt-4 bg-transparent border-b border-black/15 focus:border-brand-navy focus:outline-none py-2 text-sm"
              data-testid="cb-name"
            />
            <input
              required
              type="tel"
              placeholder={L.phone}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full mt-4 bg-transparent border-b border-black/15 focus:border-brand-navy focus:outline-none py-2 text-sm"
              data-testid="cb-phone"
            />
            <input
              placeholder={L.topic}
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="w-full mt-4 bg-transparent border-b border-black/15 focus:border-brand-navy focus:outline-none py-2 text-sm"
              data-testid="cb-topic"
            />
            <button type="submit" disabled={busy} className="btn-primary mt-6 w-full justify-center text-xs disabled:opacity-60" data-testid="cb-submit">
              {busy ? "…" : L.send}
            </button>
          </motion.form>
        ) : null}
      </AnimatePresence>

      <motion.button
        initial={false}
        animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 20, pointerEvents: visible ? "auto" : "none" }}
        transition={{ duration: 0.3 }}
        type="button"
        onClick={() => { setOpen((v) => !v); setCallback(false); }}
        aria-label="Contact"
        className="w-14 h-14 bg-brand-navy text-white flex items-center justify-center hover:bg-brand-gold transition-colors shadow-2xl relative"
        data-testid="fc-toggle"
      >
        {open ? <X size={20} strokeWidth={1.5} /> : <MessageCircle size={20} strokeWidth={1.5} />}
        {!open ? (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-brand-gold">
            <span className="absolute inset-0 rounded-full bg-brand-gold animate-ping opacity-60" />
          </span>
        ) : null}
      </motion.button>
    </div>
  );
};

export default FloatingContact;
